import { z } from "zod";
export const API_PRODUCT_REQUEST_STATUS_ENUM = [
    "AI Error",
    "Invalid API route",
    "No active subscription",
    "Requests exhausted",
    "Pending",
    "Server Error",
    "Success",
];
export const apiProductRequestStatusZod = z.enum(API_PRODUCT_REQUEST_STATUS_ENUM);
export const API_PRODUCTS = [
    {
        apiRoute: "voice-sessions",
        name: "Save Voice Session",
        sku: "voice_session_create",
        unitSize: 100,
        validityDays: 30,
    },
];
export const apiProductSkuEnum = API_PRODUCTS.map((product) => product.sku);
export const apiProductSkuZod = z.enum(apiProductSkuEnum);
export const API_REQUEST_METHOD_ENUM = ["GET", "POST", "PUT", "DELETE"];
export const apiRequestMethodZod = z.enum(API_REQUEST_METHOD_ENUM);
// Lifecycle of a conversation.
//
// `ringing` is the control plane's: a call has been answered (or placed) and
// the session id minted, but nothing has connected to the runtime yet. It is
// the status that makes an ATTEMPT visible — a carrier that fetches
// `answer_url`, takes the `<Stream>` XML and never opens the socket used to
// leave no row at all, and the call was invisible in every dashboard.
//
// `live` rows are owned by the Elixir runtime's session process until it
// writes the terminal state back. Both are provisional; only `completed` and
// `failed` are terminal.
export const VOICE_SESSION_STATUS_ENUM = ["ringing", "live", "completed", "failed"];
export const voiceSessionStatusZod = z.enum(VOICE_SESSION_STATUS_ENUM);
/**
 * The two statuses that mean "not finished yet", named once.
 *
 * Every guard that decides whether a later write may overwrite a session row
 * has to agree on this set, and the settling write in `control_plane.grpc.ts`
 * is the one that matters: it used to compare against `"live"` literally, so
 * the day `ringing` was added a checkpointless call would have been treated
 * as a duplicate delivery and its real transcript dropped on the floor.
 */
export const PROVISIONAL_VOICE_SESSION_STATUSES = ["ringing", "live"];
export function isProvisionalVoiceSessionStatus(status) {
    return PROVISIONAL_VOICE_SESSION_STATUSES.includes(status);
}
export const FILE_TABLE_NAME_ENUM = ["voiceSessions"];
export const fileTableNameZod = z.enum(FILE_TABLE_NAME_ENUM);
// What a stored file IS, which decides where it renders and what deletes it.
//
// `voice_sample` is a recording of a human voice a user uploaded themselves —
// reference audio, or the caller's own recording of the call, which is the one
// artefact in this system measured at the caller's ear. It is deliberately a
// separate type rather than another `attachment` because of where it RENDERS:
// attachments go in the session's thumbnail gallery and a voice recording
// needs a player. The type decides rendering; it does not decide visibility.
//
// **Both types are team-visible.** Nothing here is scoped to whoever uploaded
// it — see `files.getByTableId`, which was narrowed that way once and made a
// caller's recording invisible to the colleague reviewing the same call.
export const FILE_TYPE_ENUM = ["attachment", "voice_sample"];
export const fileTypeZod = z.enum(FILE_TYPE_ENUM);
export const TEAM_MEMBER_ROLE_ENUM = ["Owner", "Admin", "Member"];
export const teamMemberRoleZod = z.enum(TEAM_MEMBER_ROLE_ENUM);
export const THEME_SETTING_ENUM = ["dark", "light", "system"];
export const themeSettingZod = z.enum(THEME_SETTING_ENUM);
export const WEBHOOK_STATUS_ENUM = ["Pending", "Sent", "Failed"];
export const webhookStatusZod = z.enum(WEBHOOK_STATUS_ENUM);
export const PG_TBUS_TASK_STATUS_ENUM = [
    "pending",
    "active",
    "completed",
    "failed",
    "cancelled",
];
export const pgTbusTaskStatusZod = z.enum(PG_TBUS_TASK_STATUS_ENUM);
export const FEATURE_FLAG_SCOPE_ENUM = ["global", "team"];
export const featureFlagScopeZod = z.enum(FEATURE_FLAG_SCOPE_ENUM);
// Device platform reported by the frontend at push-register time.
// Broad enough to cover every OS a Web Push subscription can originate
// from, with `other` as a catchall so unknown UAs don't fail validation.
// Extend as new platforms show up (visionos, harmonyos, fireos, etc.).
export const DEVICE_PLATFORM_ENUM = [
    "ios",
    "android",
    "macos",
    "windows",
    "linux",
    "chromeos",
    "other",
];
export const devicePlatformZod = z.enum(DEVICE_PLATFORM_ENUM);
// Why a push_devices row was soft-deleted. Separates voluntary churn
// (user toggled off / logged out) from involuntary (upstream pruned).
// Extend as new detachment paths land (e.g. "browser_rotated" when we
// handle pushsubscriptionchange server-side).
export const DEVICE_DEACTIVATION_REASON_ENUM = [
    "user_revoked",
    "novu_pruned",
    "subscriber_deleted",
];
export const deviceDeactivationReasonZod = z.enum(DEVICE_DEACTIVATION_REASON_ENUM);
// A batch-call batch's own lifecycle. No "paused" yet — Phase 0 of
// docs/batch-calling-plan.md ships stop-only; pause is Phase 3.
export const BATCH_STATUS_ENUM = ["draft", "running", "stopped", "completed"];
export const batchStatusZod = z.enum(BATCH_STATUS_ENUM);
// One contact's progress through a batch's dispatch/retry cycle.
// `queued` means an open `outbound_call_requests` row is waiting for a
// channel on this contact's behalf; `calling` is the claimed-but-not-yet-
// reconciled state set when that request is granted and the call actually
// placed, so a second tick (or replica) never double-dials the same row.
export const BATCH_CONTACT_STATUS_ENUM = [
    "pending",
    "queued",
    "calling",
    "retry_scheduled",
    "completed",
    "exhausted",
];
export const batchContactStatusZod = z.enum(BATCH_CONTACT_STATUS_ENUM);
// Which carrier a phone number lives at. Grows by integration work — the
// pg column is a plain string, this enum is the actual gatekeeper.
export const TELEPHONY_PROVIDER_ENUM = ["vobiz"];
export const telephonyProviderZod = z.enum(TELEPHONY_PROVIDER_ENUM);
// The channel-grant billing ledger's vocabulary. Append-only — see
// docs/batch-calling-plan.md § Channel manager & call admission.
export const CHANNEL_GRANT_EVENT_ENUM = ["purchased", "removed", "assigned", "unassigned"];
export const channelGrantEventZod = z.enum(CHANNEL_GRANT_EVENT_ENUM);
export const OUTBOUND_CALL_REQUEST_SOURCE_ENUM = ["manual", "batch"];
export const outboundCallRequestSourceZod = z.enum(OUTBOUND_CALL_REQUEST_SOURCE_ENUM);
// `granted` is the claimed-but-not-yet-placed state one grant tick holds
// briefly. Terminal states: dispatched, expired, cancelled, failed.
export const OUTBOUND_CALL_REQUEST_STATUS_ENUM = [
    "queued",
    "granted",
    "dispatched",
    "expired",
    "cancelled",
    "failed",
];
export const outboundCallRequestStatusZod = z.enum(OUTBOUND_CALL_REQUEST_STATUS_ENUM);
/**
 * The three legs of a conversation, in the order a turn visits them.
 *
 * This is the key the provider catalogue is organised by, and the same word
 * the realtime runtime uses (`VoicelyRealtime.Provider.Capability`'s `kind`).
 * A vendor that serves two legs — Smallest serves all three — is two or three
 * rows, because what it can do differs per leg and a single row would have to
 * average them.
 */
export const PROVIDER_KIND_ENUM = ["stt", "llm", "tts"];
export const providerKindZod = z.enum(PROVIDER_KIND_ENUM);
//# sourceMappingURL=enums.zod.js.map